import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useApi } from '../lib/api.js';

/**
 * Fetches the current user's record from the backend /me endpoint.
 * Includes role (owner/dispatcher/viewer), email, full name.
 *
 * Clerk gives us the authentication ("who"); the backend gives us the
 * authorisation ("what can they do"). This hook merges the two.
 *
 * Returns { data, isLoading, isError }. Also returns role convenience
 * flags so components can do `isOwner && <deleteButton />`.
 */
export function useCurrentUser() {
    const { isSignedIn } = useAuth();
    const api = useApi();

    const query = useQuery({
        queryKey: ['me'],
        queryFn: () => api.get('/me'),
        enabled: isSignedIn === true,
        staleTime: 5 * 60 * 1000, // Role rarely changes; cache 5 min
    });

    const user = query.data;
    return {
        ...query,
        user,
        isOwner: user?.role === 'owner',
        isDispatcher: user?.role === 'dispatcher',
        isViewer: user?.role === 'viewer',
        isAdmin: user?.role === 'owner' || user?.role === 'dispatcher',
    };
}
